// Pure, zero-dependency (native `Intl` only) date/time formatting for the
// storefront. No zod imports — safe to pull through the `@tripcart/shared/format`
// client entry point without dragging the wire schemas into a route's bundle.
//
// Single source of truth for turning the operator's Site Settings
// (`dateFormat` / `timeFormat` / `timezone` / `siteLanguage`) into the exact
// strings travellers see. Mirrors how `formatMoney` centralises currency.
//
// Two kinds of value, deliberately handled differently:
//   - a **civil date** (a `YYYY-MM-DD` calendar day, e.g. a departure date):
//     rendered as-is, with NO timezone shift (preserves the west-of-UTC fix in
//     `traveler-web/lib/local-date`).
//   - a **timestamp** (a real instant, e.g. a booking's `createdAt`): resolved
//     in the agency's IANA timezone, so every traveller sees the tour's local
//     time regardless of their own device timezone.

/** Operator date-format tokens — keep in sync with `SITE_DATE_FORMATS`. */
export type StorefrontDateFormat =
  | "MM/DD/YYYY"
  | "DD/MM/YYYY"
  | "YYYY-MM-DD"
  | "D MMM YYYY"
  | "MMMM D, YYYY";

export type StorefrontTimeFormat = "12h" | "24h";

export interface StorefrontFormatSettings {
  /** Operator token; absent → legacy short `MMM D, YYYY` (current behaviour). */
  dateFormat?: StorefrontDateFormat | string;
  /** Absent → `24h` (matches the storefront's current raw 24h times). */
  timeFormat?: StorefrontTimeFormat | string;
  /** IANA zone — applied only to real timestamps (`mode: "timestamp"`). */
  timezone?: string;
  /** BCP-47 locale for month + weekday names (the operator's site language). */
  locale?: string;
}

export interface FormatStorefrontDateOptions extends StorefrontFormatSettings {
  /** `date-only` (default) = civil date, no tz shift. `timestamp` = resolve in `timezone`. */
  mode?: "date-only" | "timestamp";
  /** Prepend the weekday name (`"Mon, …"` / `"Monday, …"`). */
  weekday?: "short" | "long";
  /** Append the clock time — only meaningful for `mode: "timestamp"`. */
  withTime?: boolean;
  /**
   * Force a spelled month-name presentation (`"July 15"`), IGNORING the numeric
   * `dateFormat` token — for friendly / prose labels (a "next available" hint, a
   * booking's departure day, a coarse review month) that must read as words,
   * never as `15/07/2026`. Width picks `MMM` vs `MMMM`. Without it the numeric
   * `dateFormat` token drives the output.
   */
  monthStyle?: "short" | "long";
  /** Include the year. Default `true`; a same-year range's start date, and
   *  near-term friendly hints, set `false`. `YYYY-MM-DD` ignores it — there is
   *  no sub-year form of that token. */
  withYear?: boolean;
  /** Drop the day → a coarse `Month YYYY` label (review dates). Honoured only with `monthStyle`. */
  omitDay?: boolean;
}

interface DateParts {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number; // 0–23
  minute: number;
  /** The reference `Date` + zone used to derive localized month/weekday names. */
  ref: Date;
  timeZone?: string;
}

const CIVIL_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse a `YYYY-MM-DD` calendar day at local civil midnight; other strings via
 *  native parsing. Mirrors `traveler-web/lib/local-date.parseLocalDate`. */
function parseCivil(value: string): Date {
  const m = CIVIL_RE.exec(value);
  return m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(value);
}

function resolveParts(
  value: Date | string,
  mode: "date-only" | "timestamp",
  timeZone?: string,
): DateParts | null {
  const date =
    typeof value === "string"
      ? mode === "date-only"
        ? parseCivil(value)
        : new Date(value)
      : value;
  if (Number.isNaN(date.getTime())) return null;

  if (mode === "timestamp" && timeZone) {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
        .formatToParts(date)
        .map((p) => [p.type, p.value]),
    );
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      // Intl renders midnight as "24" in some engines; normalise to 0.
      hour: Number(parts.hour) % 24,
      minute: Number(parts.minute),
      ref: date,
      timeZone,
    };
  }

  // Civil date — read the local calendar parts, no zone shift.
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    ref: date,
  };
}

/** Operators store WordPress-core locale codes (`fr_FR`, `de_DE_formal`,
 *  `pt_BR`), but `Intl` needs BCP-47 (`fr-FR`, `de-DE`, `pt-BR`) — an underscore
 *  tag throws `RangeError` in the `Intl.DateTimeFormat` ctor and silently falls
 *  back to English, so localized month/weekday names never appeared. Keep only
 *  the language + region subtags and hyphenate: `de_DE_formal` → `de-DE`. */
/** Local, weaker sibling of `toBcp47Locale` in `i18n/locale-tag.ts`.
 *
 *  NOT exported, deliberately: anything outside this file that needs a BCP-47
 *  tag — `Intl.ListFormat` for guide languages, `toLocaleString` for a review
 *  count — should import `toBcp47Locale`, which canonicalises through
 *  `Intl.getCanonicalLocales` and strips WordPress's variant suffixes
 *  (`de_DE_formal`, `pt_PT_ao90`) that this one passes through. Two public
 *  helpers doing the same job differently is how the almost-right copy gets
 *  made. */
function toBcp47(locale: string | undefined): string {
  if (!locale) return "en";
  return locale.split("_").slice(0, 2).join("-");
}

function namePart(
  parts: DateParts,
  locale: string | undefined,
  option: Intl.DateTimeFormatOptions,
): string {
  const opts: Intl.DateTimeFormatOptions = { ...option };
  if (parts.timeZone) opts.timeZone = parts.timeZone;
  // A malformed locale tag throws in the `Intl.DateTimeFormat` ctor — fall back
  // to English rather than crash a render.
  try {
    return new Intl.DateTimeFormat(toBcp47(locale), opts).format(parts.ref);
  } catch {
    return new Intl.DateTimeFormat("en", opts).format(parts.ref);
  }
}

/** Appends `sep` + `yyyy` unless the caller suppressed the year. Factored out
 *  of `assembleDate` so each switch case stays a plain return, not a ternary —
 *  five added ternaries pushed that function's own complexity over budget. */
function withOptionalYear(
  base: string,
  yyyy: string,
  sep: string,
  withYear: boolean,
): string {
  return withYear ? `${base}${sep}${yyyy}` : base;
}

/**
 * `withYear: false` drops the year from every token's output — used to show
 * just one year on a same-year date range ("Aug 15 – Aug 31, 2026") instead of
 * repeating it on both ends. `YYYY-MM-DD` has no sub-year form, so it ignores
 * the flag rather than emit a truncated, unparseable date.
 */
function assembleDate(
  parts: DateParts,
  dateFormat: string | undefined,
  locale: string | undefined,
  withYear = true,
): string {
  const yyyy = String(parts.year).padStart(4, "0");
  const mm = String(parts.month).padStart(2, "0");
  const dd = String(parts.day).padStart(2, "0");
  const d = String(parts.day);

  switch (dateFormat) {
    case "MM/DD/YYYY":
      return withOptionalYear(`${mm}/${dd}`, yyyy, "/", withYear);
    case "DD/MM/YYYY":
      return withOptionalYear(`${dd}/${mm}`, yyyy, "/", withYear);
    case "YYYY-MM-DD":
      return `${yyyy}-${mm}-${dd}`;
    case "D MMM YYYY":
      return withOptionalYear(
        `${d} ${namePart(parts, locale, { month: "short" })}`,
        yyyy,
        " ",
        withYear,
      );
    case "MMMM D, YYYY":
      return withOptionalYear(
        `${namePart(parts, locale, { month: "long" })} ${d}`,
        yyyy,
        ", ",
        withYear,
      );
    default:
      // Legacy default preserves the pre-feature storefront output ("Aug 31, 2026").
      return withOptionalYear(
        `${namePart(parts, locale, { month: "short" })} ${d}`,
        yyyy,
        ", ",
        withYear,
      );
  }
}

/**
 * A spelled "Month Day" presentation that IGNORES the numeric `dateFormat`
 * token — for friendly / prose labels that always read as words. `monthStyle`
 * picks the month-name width; `withYear` / `omitDay` trim the trailing
 * components. Restores the pre-datetime-feature storefront wording (e.g. a
 * "next available" hint or a coarse `MMM YYYY` review month).
 */
function assembleFriendlyDate(
  parts: DateParts,
  monthStyle: "short" | "long",
  locale: string | undefined,
  opts: { withYear: boolean; omitDay: boolean },
): string {
  const name = namePart(parts, locale, { month: monthStyle });
  const yyyy = String(parts.year).padStart(4, "0");
  const d = String(parts.day);
  // `omitDay` used to return here unconditionally, which made `withYear`
  // UNREACHABLE whenever it was set — so `{ omitDay: true, withYear: false }`,
  // the combination that means "just the month", silently printed the year
  // anyway. It reads as a supported option because both flags are documented
  // and independently typed; it simply never ran.
  //
  // `withYear` defaults to `true` at every caller, so the long-standing
  // `{ omitDay: true }` behaviour ("Aug 2026") is untouched. Only the
  // previously-dead combination changes.
  if (opts.omitDay) return opts.withYear ? `${name} ${yyyy}` : name;
  if (!opts.withYear) return `${name} ${d}`;
  return `${name} ${d}, ${yyyy}`;
}

function assembleTime(
  hour: number,
  minute: number,
  timeFormat: string | undefined,
): string {
  const mm = String(minute).padStart(2, "0");
  if (timeFormat === "12h") {
    const period = hour < 12 ? "AM" : "PM";
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${mm} ${period}`;
  }
  // Default + "24h": zero-padded 24-hour clock (the storefront's current style).
  return `${String(hour).padStart(2, "0")}:${mm}`;
}

/**
 * Format a date (or timestamp) per the agency's Site Settings.
 * Returns `""` for an unparseable value (callers render nothing, as today).
 */
export function formatStorefrontDate(
  value: Date | string,
  opts: FormatStorefrontDateOptions = {},
): string {
  const mode = opts.mode ?? "date-only";
  const parts = resolveParts(value, mode, opts.timezone);
  if (!parts) return "";

  const withYear = opts.withYear ?? true;
  let out = opts.monthStyle
    ? assembleFriendlyDate(parts, opts.monthStyle, opts.locale, {
        withYear,
        omitDay: opts.omitDay ?? false,
      })
    : assembleDate(parts, opts.dateFormat, opts.locale, withYear);
  if (opts.weekday) {
    out = `${namePart(parts, opts.locale, { weekday: opts.weekday })}, ${out}`;
  }
  if (opts.withTime && mode === "timestamp") {
    out = `${out}, ${assembleTime(parts.hour, parts.minute, opts.timeFormat)}`;
  }
  return out;
}

/**
 * Re-format a wall-clock `HH:mm` string (e.g. a departure start time, already
 * in the tour's local time) per the agency's `timeFormat`. No timezone math —
 * these strings carry no instant. Unrecognised input is returned unchanged.
 */
export function formatStorefrontTime(
  value: string,
  opts: Pick<StorefrontFormatSettings, "timeFormat"> = {},
): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return value;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return value;
  return assembleTime(hour, minute, opts.timeFormat);
}
